import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"
import _imports_0 from '@/assets/icons/apple-app-store-button.svg'
import _imports_1 from '@/assets/icons/google-play-store-button.svg'


const _hoisted_1 = ["data-test-id"]
const _hoisted_2 = {
  key: 0,
  class: "w-12 h-12 md:mr-4 flex-shrink-0"
}
const _hoisted_3 = { class: "text-racq-dark-grey text-lg-bold text-center md:text-left" }
const _hoisted_4 = {
  key: 1,
  class: "text-racq-med-grey text-m md:text-base text-center md:text-left"
}
const _hoisted_5 = {
  key: 0,
  class: "flex flex-row flex-wrap items-center flex-shrink-0 pt-4 gap-4 justify-center"
}
const _hoisted_6 = {
  key: 0,
  class: "m-auto flex"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = {
  key: 1,
  class: "m-auto flex"
}
const _hoisted_9 = ["src"]
const _hoisted_10 = {
  key: 2,
  class: "flex justify-center pt-4"
}
const _hoisted_11 = ["href"]
const _hoisted_12 = ["href"]

import { ImageField } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import { RichText, Image, Field, ComponentRendering, Link } from '@sitecore-jss/sitecore-jss-vue';
import { computed, onMounted, ref } from 'vue';
import { usePageStore } from '@/store';
import { LinkField } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';
import { isMobileDevice } from '@/utils/dom-helpers';
import { downloadAppleMembershipCard, downloadGoogleMembershipCard } from '@/constants/forms-api.const';
import { useFeatureTogglesStore } from '@/store/feature-toggles/featureTogglesStore';
import { platform } from '@/utils/device-helper';

export interface IAppBannerProps {
  title?: string;
  subTitle?: string;
  icon?: ImageField;
  greyBg?: boolean;
  purpleBg?: boolean;
  appleDownloadLink?: string;
  appleDownloadLinkDesktop?: string;
  googleDownloadLink?: string;
  googleDownloadLinkDesktop?: string;
  showAppStoreButtons?: boolean;
  showPrimaryButton?: boolean;
  showSecondaryButton?: boolean;
  fields?: Record<string, Field<string | boolean> | ImageField | LinkField>;
  rendering?: ComponentRendering;
  addMembershipCardDesktopLink?: LinkField;
  showAddToWalletLink?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppBanner',
  props: {
    title: {},
    subTitle: {},
    icon: {},
    greyBg: { type: Boolean },
    purpleBg: { type: Boolean },
    appleDownloadLink: {},
    appleDownloadLinkDesktop: {},
    googleDownloadLink: {},
    googleDownloadLinkDesktop: {},
    showAppStoreButtons: { type: Boolean },
    showPrimaryButton: { type: Boolean },
    showSecondaryButton: { type: Boolean },
    fields: {},
    rendering: {},
    addMembershipCardDesktopLink: {},
    showAddToWalletLink: { type: Boolean }
  },
  emits: ['buttonClick'],
  setup(__props: any, { emit: __emit }) {

const title = computed(() => (props.fields?.title?.value as string) ?? props.title);
const subTitle = computed(() => (props.fields?.subTitle?.value as string) ?? props.subTitle);
const icon = computed(() => (props.fields?.icon as ImageField) ?? props.icon);
const greyBg = computed(() => (props.fields?.greyBg?.value as boolean) ?? props.greyBg);
const purpleBg = computed(() => (props.fields?.purpleBg?.value as boolean) ?? props.purpleBg);
const devicePlatform = computed(() => platform());
const showAddToWallet = computed(
  () => (props.fields?.showAddToWalletLink?.value as boolean) ?? props.showAddToWalletLink
);
const addToWalletLinkDesktop = computed(
  () => props.fields?.addMembershipCardDesktopLink ?? props.addMembershipCardDesktopLink
);

const appleDownloadLink = computed(() => {
  const desktopLink = (props.fields?.appleDownloadLinkDesktop?.value as string) ?? props.appleDownloadLinkDesktop;
  if (!desktopLink || isMobileDevice())
    return (props.fields?.appleDownloadLink?.value as string) ?? props.appleDownloadLink;
  return desktopLink;
});
const googleDownloadLink = computed(() => {
  const desktopLink = (props.fields?.googleDownloadLinkDesktop?.value as string) ?? props.googleDownloadLinkDesktop;
  if (!desktopLink || isMobileDevice())
    return (props.fields?.googleDownloadLink?.value as string) ?? props.googleDownloadLink;
  return desktopLink;
});
const showAppStoreButtons = computed(
  () => (props.fields?.showAppStoreButtons?.value as boolean) ?? props.showAppStoreButtons
);
const primaryBtn = computed(() => props.fields?.primaryButton as LinkField);
const secondaryBtn = computed(() => props.fields?.secondaryButton as LinkField);
const showPrimaryBtn = computed(() => props.fields?.showPrimaryButton?.value ?? props.showPrimaryButton);
const showSecondaryBtn = computed(() => props.fields?.showSecondaryButton?.value ?? props.showPrimaryButton);
const bgClass = computed(() => {
  if (purpleBg.value) return 'bg-racq-light-purple';
  return greyBg.value ? 'bg-racq-anti-flash-white' : 'bg-racq-info-light-blue';
});

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = __props;

const featureToggleStore = useFeatureTogglesStore();
const outerClass = props.rendering?.params?.Styles;
const isGoogleWalletEnabled = ref(false);
const isAppleWalletEnabled = ref(false);
const inlineStyle = props.rendering?.params?.inlineStyle;
const dataTestId = usePageStore().dataTestId(props.rendering);
const iconMap = {
  appleWalletIcon: require('@/assets/icons/apple_wallet.svg'),
  googleWalletIcon: require('@/assets/icons/google_wallet.svg'),
};
const emit = __emit;

const inlineBanner = computed(() => !subTitle.value);
const stackedBanner = computed(() => subTitle.value);

onMounted(async () => {
  isAppleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardAppleWallet');
  isGoogleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardGoogleWallet');
});

const emitPrimaryClick = (e: MouseEvent) => {
  if (isEmptyLink(primaryBtn.value?.value?.href)) e.preventDefault();
  emit('buttonClick', 'primaryAppBannerButton');
};

const emitSecondaryClick = (e: MouseEvent) => {
  if (isEmptyLink(secondaryBtn.value?.value?.href)) e.preventDefault();
  emit('buttonClick', 'secondaryAppBannerButton');
};

function handleWalletClick(walletPlatform: 'apple' | 'android') {
  if (walletPlatform === 'android') {
    window.location.href = downloadGoogleMembershipCard;
  } else if (walletPlatform === 'apple') {
    alert('Redirecting to Apple Wallet');
    window.location.href = downloadAppleMembershipCard;
  } else {
    alert('Wallet not supported on this device.');
  }
}

const isEmptyLink = (href: string | undefined) => !href || href === 'http://';

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      'app-banner px-6 py-6 md:px-10 md:py-6 items-center rounded-lg app-banner-max-w-sm app-banner-w-sm md:app-banner-max-w-lg md:app-banner-w-lg flex-col flex justify-start items-start',
      {
        'justify-center md:flex-row md:gap-8': inlineBanner.value,
        'md:flex-col': stackedBanner.value,
      },
      _unref(outerClass),
      bgClass.value,
    ]),
    style: _normalizeStyle(_unref(inlineStyle)),
    "data-test-id": _unref(dataTestId)
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(['flex items-center flex-col', { 'md:flex-col': stackedBanner.value, 'md:flex-row': inlineBanner.value }])
    }, [
      (icon.value?.value?.src)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_unref(Image), {
              media: icon.value,
              class: "w-12 h-12"
            }, null, 8, ["media"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(RichText), {
          field: { value: title.value }
        }, null, 8, ["field"])
      ]),
      (subTitle.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(_unref(RichText), {
              field: { value: subTitle.value }
            }, null, 8, ["field"])
          ]))
        : _createCommentVNode("", true)
    ], 2),
    (showAddToWallet.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (isAppleWalletEnabled.value && devicePlatform.value === 'apple')
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("button", {
                  type: "button",
                  "aria-label": "Apple add to wallet icon",
                  onClick: _cache[0] || (_cache[0] = () => handleWalletClick('apple'))
                }, [
                  _createElementVNode("img", {
                    src: iconMap.appleWalletIcon,
                    alt: "Apple add to wallet icon",
                    class: "w-full"
                  }, null, 8, _hoisted_7)
                ])
              ]))
            : _createCommentVNode("", true),
          (isGoogleWalletEnabled.value && (devicePlatform.value === 'apple' || devicePlatform.value === 'android'))
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("button", {
                  type: "button",
                  "aria-label": "Google add to wallet icon",
                  onClick: _cache[1] || (_cache[1] = () => handleWalletClick('android'))
                }, [
                  _createElementVNode("img", {
                    src: iconMap.googleWalletIcon,
                    alt: "Google add to wallet icon",
                    class: "w-full"
                  }, null, 8, _hoisted_9)
                ])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createVNode(_unref(Link), {
                  class: "text-m md:text-base",
                  field: addToWalletLinkDesktop.value
                }, null, 8, ["field"])
              ]))
        ]))
      : _createCommentVNode("", true),
    (showAppStoreButtons.value && (appleDownloadLink.value || googleDownloadLink.value))
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass([
          'flex flex-row pt-4 items-center flex-shrink-0 gap-4',
          { 'justify-center': stackedBanner.value, 'md:pt-0': inlineBanner.value },
        ])
        }, [
          (appleDownloadLink.value)
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                href: appleDownloadLink.value,
                target: "_blank",
                class: "inline-block"
              }, _cache[2] || (_cache[2] = [
                _createElementVNode("img", {
                  src: _imports_0,
                  alt: "apple app store icon",
                  class: "inline-block"
                }, null, -1)
              ]), 8, _hoisted_11))
            : _createCommentVNode("", true),
          (googleDownloadLink.value)
            ? (_openBlock(), _createElementBlock("a", {
                key: 1,
                href: googleDownloadLink.value,
                target: "_blank",
                class: "inline-block"
              }, _cache[3] || (_cache[3] = [
                _createElementVNode("img", {
                  src: _imports_1,
                  alt: "google play store icon",
                  class: "inline-block"
                }, null, -1)
              ]), 8, _hoisted_12))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    (showPrimaryBtn.value || showSecondaryBtn.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 2,
          class: _normalizeClass([
          'flex flex-row pt-4 items-center flex-shrink-0 gap-4',
          { 'justify-center': stackedBanner.value, 'md:pt-0': inlineBanner.value },
        ])
        }, [
          (showPrimaryBtn.value)
            ? (_openBlock(), _createBlock(_unref(Link), {
                key: 0,
                "aria-label": primaryBtn.value?.value?.title,
                field: primaryBtn.value,
                class: "flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm btn-primary",
                onClick: emitPrimaryClick
              }, null, 8, ["aria-label", "field"]))
            : _createCommentVNode("", true),
          (showSecondaryBtn.value)
            ? (_openBlock(), _createBlock(_unref(Link), {
                key: 1,
                "aria-label": secondaryBtn.value?.value?.title,
                field: secondaryBtn.value,
                class: "flex px-3 py-1.5 justify-center items-center self-stretch text-base btn btn-sm btn-secondary",
                onClick: emitSecondaryClick
              }, null, 8, ["aria-label", "field"]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true)
  ], 14, _hoisted_1))
}
}

})